import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CROP_COLOR_MAP, FALLBACK_SEASON_COLORS } from '../constants/crop-colors';
import { environment } from '@root/src/environments/environment';

export interface MapState {
  center: [number, number];
  zoom: number;
  basemapType: string;
  showRegionNames: boolean;
  showFieldBoundaries: boolean;
  showMonitoringPoints: boolean;
}

export interface DrawingState {
  isDrawing: boolean;
  drawingType: 'point' | 'polygon' | 'field' | null;
  tempMarkers: any[];
  tempPolygon: any | null;
}

@Injectable({
  providedIn: 'root'
})
export class MapOperationsService {
  private mapInstanceSubject = new BehaviorSubject<any>(null);
  public mapInstance$ = this.mapInstanceSubject.asObservable();

  private mapStateSubject = new BehaviorSubject<MapState>({
    center: [environment.map.lat, environment.map.lng],
    zoom: 6,
    basemapType: 'OSM',
    showRegionNames: false,
    showFieldBoundaries: false,
    showMonitoringPoints: true
  });
  public mapState$ = this.mapStateSubject.asObservable();

  private drawingStateSubject = new BehaviorSubject<DrawingState>({
    isDrawing: false,
    drawingType: null,
    tempMarkers: [],
    tempPolygon: null
  });
  public drawingState$ = this.drawingStateSubject.asObservable();

  private markersLayerSubject = new BehaviorSubject<any>(null);
  public markersLayer$ = this.markersLayerSubject.asObservable();

  private polygonsLayerSubject = new BehaviorSubject<any>(null);
  public polygonsLayer$ = this.polygonsLayerSubject.asObservable();

  constructor(private translate: TranslateService) {}

  setMapInstance(map: any): void {
    this.mapInstanceSubject.next(map);
  }

  updateMapState(state: Partial<MapState>): void {
    const currentState = this.mapStateSubject.value;
    this.mapStateSubject.next({ ...currentState, ...state });
  }

  updateDrawingState(state: Partial<DrawingState>): void {
    const currentState = this.drawingStateSubject.value;
    this.drawingStateSubject.next({ ...currentState, ...state });
  }

  setMarkersLayer(layer: any): void {
    this.markersLayerSubject.next(layer);
  }

  setPolygonsLayer(layer: any): void {
    this.polygonsLayerSubject.next(layer);
  }

  zoomToLocation(lat: number, lng: number, zoomLevel: number = 15): void {
    const map = this.mapInstanceSubject.value;
    if (map && map.map) {
      const L = (window as any).L;
      map.map.setView([lat, lng], zoomLevel, {
        animate: true,
        duration: 1
      });
    }
  }

  fitBounds(bounds: any): void {
    const map = this.mapInstanceSubject.value;
    if (map && map.map) {
      map.map.fitBounds(bounds, {
        padding: [50, 50],
        animate: true,
        duration: 1
      });
    }
  }

  addMarkerToMap(lat: number, lng: number, options: any = {}): any {
    const map = this.mapInstanceSubject.value;
    if (!map || !map.map) return null;

    const L = (window as any).L;
    const marker = L.marker([lat, lng], options);

    const markersLayer = this.markersLayerSubject.value;
    if (markersLayer) {
      markersLayer.addLayer(marker);
    }

    return marker;
  }

  addPolygonToMap(coordinates: any[], options: any = {}): any {
    const map = this.mapInstanceSubject.value;
    if (!map || !map.map) return null;

    const L = (window as any).L;
    const polygon = L.polygon(coordinates, options);

    const polygonsLayer = this.polygonsLayerSubject.value;
    if (polygonsLayer) {
      polygonsLayer.addLayer(polygon);
    }

    return polygon;
  }

  clearDrawing(): void {
    const drawingState = this.drawingStateSubject.value;
    const map = this.mapInstanceSubject.value;

    if (!map || !map.map) return;

    drawingState.tempMarkers.forEach((marker: any) => {
      map.map.removeLayer(marker);
    });

    if (drawingState.tempPolygon) {
      map.map.removeLayer(drawingState.tempPolygon);
    }

    this.updateDrawingState({
      isDrawing: false,
      drawingType: null,
      tempMarkers: [],
      tempPolygon: null
    });
  }

  changeBasemap(basemapType: string): void {
    const map = this.mapInstanceSubject.value;
    if (!map || !map.map) return;

    const L = (window as any).L;
    let basemapUrl = '';
    let attribution = '';

    switch (basemapType) {
      case 'OSM':
        basemapUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
        attribution = '© OpenStreetMap contributors';
        break;
      case 'Satellite':
        basemapUrl = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
        attribution = '© Esri';
        break;

      case 'Terrain':
        basemapUrl = 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png';
        attribution = '© OpenTopoMap';
        break;
      case 'Dark':
        basemapUrl = 'https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png';
        attribution = '© Stadia Maps';
        break;
      default:
        basemapUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
        attribution = '© OpenStreetMap contributors';
    }

    map.map.eachLayer((layer: any) => {
      if (layer instanceof L.TileLayer) {
        map.map.removeLayer(layer);
      }
    });

    L.tileLayer(basemapUrl, {
      attribution: attribution,
      maxZoom: 19
    }).addTo(map.map);

    this.updateMapState({ basemapType });
  }

  calculateDistance(point1: [number, number], point2: [number, number]): number {
    const R = 6371;
    const dLat = (point2[0] - point1[0]) * Math.PI / 180;
    const dLon = (point2[1] - point1[1]) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(point1[0] * Math.PI / 180) * Math.cos(point2[0] * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  getMapBounds(): any {
    const map = this.mapInstanceSubject.value;
    if (map && map.map) {
      return map.map.getBounds();
    }
    return null;
  }

  getCurrentZoom(): number {
    const map = this.mapInstanceSubject.value;
    if (map && map.map) {
      return map.map.getZoom();
    }
    return this.mapStateSubject.value.zoom;
  }

  getCurrentCenter(): [number, number] {
    const map = this.mapInstanceSubject.value;
    if (map && map.map) {
      const center = map.map.getCenter();
      return [center.lat, center.lng];
    }
    return this.mapStateSubject.value.center;
  }

  renderAllPolygons(mapInstance: any, monitoringPolygons: any[]): void {
    if (!mapInstance) {
      return;
    }

    mapInstance.clearDynamicPolygons();

    monitoringPolygons.forEach((polygon, index) => {
      if (polygon.points && polygon.points.length > 0) {
        mapInstance.addDynamicPolygon(polygon.points, {
          id: polygon.id,
          name: polygon.name,
          color: '#4CAF50',
          fillColor: 'transparent',
          fillOpacity: 0,
          weight: 2,
          opacity: 0.8,
          monitoringObjectId: polygon.monitoringObjectId
        });
      }
    });
  }

  parsePolygonData(polygonJson: any): any[] {
    let polygonData;

    if (typeof polygonJson === 'string' && polygonJson.startsWith('POLYGON')) {
      const wkt = polygonJson;
      const coordsMatch = wkt.match(/\(\((.*?)\)\)/);
      if (coordsMatch && coordsMatch[1]) {
        const coordsString = coordsMatch[1];
        polygonData = coordsString.split(',').map((coord: string) => {
          const [lon, lat] = coord.trim().split(' ').map(Number);
          return [lon, lat];
        });
      }
    } else if (typeof polygonJson === 'string') {
      try {
        polygonData = JSON.parse(polygonJson);
      } catch (e) {
        return [];
      }
    } else {
      polygonData = polygonJson;
    }

    if (Array.isArray(polygonData) && polygonData.length > 0) {
      if (polygonData[0].hasOwnProperty('lon') && polygonData[0].hasOwnProperty('lat')) {
        return polygonData.map((p: any) => [p.lon, p.lat]);
      }
      return polygonData;
    }

    return [];
  }

  getCropColor(season: any): string {
    const cropName = season.cropName || season.crop || '';
    if (cropName && CROP_COLOR_MAP[cropName]) {
      return CROP_COLOR_MAP[cropName];
    }

    if (cropName) {
      const cropNameLower = cropName.toLowerCase();
      for (const [key, color] of Object.entries(CROP_COLOR_MAP)) {
        if (key.toLowerCase().includes(cropNameLower) || cropNameLower.includes(key.toLowerCase())) {
          return color;
        }
      }
    }
    const index = parseInt(season.id || '0', 10);
    return FALLBACK_SEASON_COLORS[index % FALLBACK_SEASON_COLORS.length];
  }

  ensureSeasonPolygonPane(mapInstance: any): void {
    const map = mapInstance?.map;
    if (!map) {
      return;
    }

    if (!map.getPane('seasonPolygonPane')) {
      const seasonPane = map.createPane('seasonPolygonPane');
      seasonPane.style.zIndex = '900';
    } else {
      const existingPane = map.getPane('seasonPolygonPane');
      existingPane.style.zIndex = '900';
    }
  }

  forceDrawAllSeasons(mapInstance: any, seasons: any[]): void {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'];
    let successCount = 0;
    let failCount = 0;

    seasons.forEach((season) => {
      const polygonData = season.polygonJson || season.polygon || season.coordinates;

      if (polygonData) {
        try {
          const validCoords = this.parsePolygonData(polygonData);

          if (validCoords && validCoords.length > 0) {
            const seasonColor = this.getCropColor(season);
            const seasonTitle = season.title || `Season ${season.id}`;

            mapInstance.addDynamicPolygon(validCoords, {
              id: `force-season-${season.id}`,
              name: seasonTitle,
              type: 'force-season',
              color: seasonColor,
              fillColor: seasonColor,
              fillOpacity: 0.7,
              weight: 3,
              opacity: 1,
              className: 'season-polygon',
              pane: 'seasonPolygonPane',
              tooltip: seasonTitle,
              permanentTooltip: false,
              seasonData: season
            });

            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
          console.error(`❌ Error processing season ${season.title}:`, error);
        }
      } else {
        failCount++;
      }
    });
  }

  addPermanentLabelToSeasonPolygon(mapInstance: any, polygonId: string, title: string): void {
    if (!mapInstance?.map) return;

    const L = (window as any).L;
    if (!L) return;

    mapInstance.map.eachLayer((layer: any) => {
      if (layer.options?.id === polygonId) {
        try {
          const bounds = layer.getBounds();
          const center = bounds.getCenter();

          layer.bindTooltip(title, {
            permanent: true,
            direction: 'center',
            className: 'season-polygon-label',
            opacity: 0.9
          }).openTooltip();

        } catch (error) {
          console.error(`Failed to add label to polygon ${polygonId}:`, error);
        }
      }
    });
  }

  processAndDisplayLevel1Polygon(
    regionMap: any,
    level1Regions: any[],
    mapInstance: any,
    level1BoundaryLayer: any,
    showRegionNames: boolean,
    getPolygonColorBasedOnWeatherLayer: () => string | null,
    isDrawingMode: boolean = false
  ): void {
    if (!regionMap.polygonJson || !mapInstance?.map) return;

    const L = (window as any).L;
    const map = mapInstance.map;

    if (!map.getPane('regionBoundaryPane')) {
      const boundaryPane = map.createPane('regionBoundaryPane');
      boundaryPane.style.zIndex = '800';
    } else {
      const existingPane = map.getPane('regionBoundaryPane');
      existingPane.style.zIndex = '800';
    }

    let polygon = this.parsePolygonData(regionMap.polygonJson);

    if (polygon && polygon.length > 0) {
      const validCoords = polygon.filter((point: any) => {
        const lat = parseFloat(point[1]);
        const lng = parseFloat(point[0]);
        return !isNaN(lat) && !isNaN(lng) &&
               lat >= -90 && lat <= 90 &&
               lng >= -180 && lng <= 180;
      });

      if (validCoords.length < 3) {
        return;
      }

      const leafletCoords = validCoords.map((point: any) => [point[1], point[0]]);

      const region = level1Regions.find(r =>
        r.regionMapId === regionMap.id || r.id === regionMap.id
      );

      let regionName = 'Level 1 Region';
      if (region) {
        const currentLang = this.translate.currentLang || 'ru';
        if (region.names) {
          if (currentLang.includes('ru')) {
            regionName = region.names['ru-RU'] || region.names['ru'] || regionName;
          } else if (currentLang.includes('en')) {
            regionName = region.names['en-US'] || region.names['en'] || regionName;
          } else {
            regionName = region.names['uz-Latn-UZ'] || region.names['uz'] || regionName;
          }
        } else if (region.name) {
          regionName = region.name;
        }
      }

      try {
        const polygonColor = getPolygonColorBasedOnWeatherLayer() || '#0066CC';
        const level1Polygon = L.polygon(leafletCoords, {
          color: polygonColor,
          weight: 6,
          opacity: 1,
          fillColor: polygonColor,
          fillOpacity: 0.2,
          dashArray: [10, 5],
          interactive: !isDrawingMode,
          className: 'level1-regionmap-polygon',
          pane: 'regionBoundaryPane'
        });

        level1Polygon._tooltipContent = regionName;

        if (showRegionNames) {
          level1Polygon.bindTooltip(regionName, {
            permanent: false,
            direction: 'center',
            className: 'region-tooltip'
          });
        }

        level1Polygon.on('click', () => {
        });

        if (level1BoundaryLayer) {
          level1BoundaryLayer.addLayer(level1Polygon);
        } else {
          level1Polygon.addTo(map);
        }

      } catch (error) {
        console.error(`Error adding level 1 polygon for ${regionName}:`, error);
      }
    }
  }

  generateRandomPolygons(bounds: any, count: number, currentPolygonsCount: number = 0): any[] {
    const polygons = [];
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FD7272', '#54A0FF', '#48C9B0'];

    for (let i = 0; i < count; i++) {
      const centerLat = bounds.south + Math.random() * (bounds.north - bounds.south);
      const centerLng = bounds.west + Math.random() * (bounds.east - bounds.west);
      const size = 0.001 + Math.random() * 0.003;

      const coordinates = [
        [centerLat - size, centerLng - size],
        [centerLat - size, centerLng + size],
        [centerLat + size, centerLng + size],
        [centerLat + size, centerLng - size],
        [centerLat - size, centerLng - size]
      ];

      polygons.push({
        id: `polygon_${Date.now()}_${i}`,
        coordinates: coordinates,
        properties: {
          name: `Area ${currentPolygonsCount + i + 1}`,
          area: `${(size * size * 10000).toFixed(2)} km²`,
          color: colors[Math.floor(Math.random() * colors.length)]
        }
      });
    }

    return polygons;
  }

  handleMapClickNavigation(event: any, mapInstance: any): void {
    if (!event.polygonData || !mapInstance?.map) return;

    if (event.bounds) {
      if (event.bounds._northEast && event.bounds._southWest) {
        const centerLat = (event.bounds._northEast.lat + event.bounds._southWest.lat) / 2;
        const centerLng = (event.bounds._northEast.lng + event.bounds._southWest.lng) / 2;

        mapInstance.map.setView([centerLat, centerLng], mapInstance.map.getZoom(), {
          animate: true,
          duration: 0.5
        });
      } else {
        mapInstance.map.setView([event.lat, event.lng], mapInstance.map.getZoom(), {
          animate: true,
          duration: 0.5
        });
      }
    } else {
      mapInstance.map.setView([event.lat, event.lng], mapInstance.map.getZoom(), {
        animate: true,
        duration: 0.5
      });
    }
  }

  calculateMousePosition(event: any, fallbackPosition?: {x: number, y: number}): {x: number, y: number} {
    let mouseX = fallbackPosition?.x || 200;
    let mouseY = fallbackPosition?.y || 200;

    if (event.clientX !== undefined && event.clientY !== undefined) {
      mouseX = event.clientX;
      mouseY = event.clientY;
    } else if (event.originalEvent) {
      mouseX = event.originalEvent.clientX || event.originalEvent.pageX || mouseX;
      mouseY = event.originalEvent.clientY || event.originalEvent.pageY || mouseY;
    } else if (event.containerPoint) {
      const mapElement = document.querySelector('.leaflet-container');
      if (mapElement) {
        const mapRect = mapElement.getBoundingClientRect();
        mouseX = Math.round(mapRect.left + event.containerPoint.x);
        mouseY = Math.round(mapRect.top + event.containerPoint.y);
      }
    }

    return { x: mouseX, y: mouseY };
  }

  private doSegmentsIntersect(p1: [number, number], p2: [number, number], p3: [number, number], p4: [number, number]): boolean {
    const ccw = (A: [number, number], B: [number, number], C: [number, number]): boolean => {
      return (C[1] - A[1]) * (B[0] - A[0]) > (B[1] - A[1]) * (C[0] - A[0]);
    };

    const intersect = ccw(p1, p3, p4) !== ccw(p2, p3, p4) && ccw(p1, p2, p3) !== ccw(p1, p2, p4);

    const areAdjacent = (p1[0] === p3[0] && p1[1] === p3[1]) ||
                      (p1[0] === p4[0] && p1[1] === p4[1]) ||
                      (p2[0] === p3[0] && p2[1] === p3[1]) ||
                      (p2[0] === p4[0] && p2[1] === p4[1]);

    return intersect && !areAdjacent;
  }

  validatePolygon(points: any[]): boolean {
    if (!points || points.length < 3) {
      return false;
    }

    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];

      for (let j = i + 2; j < points.length; j++) {
        if ((i === 0 && j === points.length - 1) || j === i + 1) {
          continue;
        }

        const p3 = points[j];
        const p4 = points[(j + 1) % points.length];

        if (this.doSegmentsIntersect(p1, p2, p3, p4)) {
          return false;
        }
      }
    }

    return true;
  }

  processShapeCoordinates(shape: any): any {
    if (shape.type === 'rectangle' && shape.coordinates) {
      const coords = shape.coordinates[0];
      if (coords && coords.length >= 4) {
        shape.points = coords.map((coord: any) => [coord.lat, coord.lng]);
      }
    }
    return shape;
  }

  calculateShapeCenter(shape: any): { lat: number, lng: number } {
    let centerLat = 0, centerLng = 0;

    if (shape.points && shape.points.length > 0) {
      shape.points.forEach((point: any) => {
        centerLat += point[0];
        centerLng += point[1];
      });
      centerLat /= shape.points.length;
      centerLng /= shape.points.length;
    }

    return { lat: centerLat, lng: centerLng };
  }

  onShapesChanged(
    shapes: any[],
    previousShapeCount: number,
    callbacks: {
      onValidationError: (message: string) => void,
      onShapeProcessed: (selectedPoint: any, selectedPolygon: any) => void,
      deleteShape: (shapeId: string) => void,
      updateLocationInfo: (lat: number, lng: number) => Promise<void>,
      getWeatherData: (lat: number, lng: number) => void
    }
  ): { updatedShapes: any[], newShapeCount: number } {

    if (shapes.length > previousShapeCount) {
      const latestShape = shapes[shapes.length - 1];

      if (latestShape && (latestShape.type === 'polygon' || latestShape.type === 'rectangle')) {

        this.processShapeCoordinates(latestShape);

        if (latestShape.type === 'polygon' && latestShape.points) {
          const isValid = this.validatePolygon(latestShape.points);

          if (!isValid) {
            console.error('Invalid polygon: Self-intersecting lines detected!');
            const errorMessage = this.translate.instant('POLYGON_VALIDATION_ERROR') ||
              'Ошибка: Полигон содержит пересекающиеся линии или имеет разрывы. Пожалуйста, нарисуйте корректный полигон без самопересечений и убедитесь, что все линии соединены.';

            callbacks.onValidationError(errorMessage);

            if (latestShape.id) {
              callbacks.deleteShape(latestShape.id);
              const updatedShapes = shapes.filter(s => s.id !== latestShape.id);
              return { updatedShapes, newShapeCount: updatedShapes.length };
            }

            return { updatedShapes: shapes, newShapeCount: shapes.length };
          }
        }

        const selectedPolygonForSave = latestShape;

        if (latestShape.id) {
          setTimeout(() => {
            callbacks.deleteShape(latestShape.id);
          }, 100);
        }

        const center = this.calculateShapeCenter(latestShape);

        const selectedPointForWeather = {
          lat: center.lat,
          lng: center.lng,
          name: this.translate.instant('NEW_POLYGON') || 'New Polygon',
          isPolygon: true,
          shapeType: latestShape.type,
          shapeData: latestShape
        };

        callbacks.onShapeProcessed(selectedPointForWeather, selectedPolygonForSave);
        callbacks.updateLocationInfo(center.lat, center.lng);
        callbacks.getWeatherData(center.lat, center.lng);

        const filteredShapes = shapes.filter(s => s.id !== latestShape.id);
        return { updatedShapes: filteredShapes, newShapeCount: filteredShapes.length };
      }
    }

    return { updatedShapes: shapes, newShapeCount: shapes.length };
  }

  handleLeafletMarkerClick(
    event: any,
    leafletMarkers: any[],
    callbacks: {
      checkLocationInDistrict: (lat: number, lng: number) => Promise<string | null>,
      checkLocationInRegion: (lat: number, lng: number, flag: boolean) => Promise<string | null>,
      onMarkerProcessed: (selectedPoint: any, markerName: string) => void,
      getWeatherData: (lat: number, lng: number) => void,
      updateCurrentWeatherDate: () => void,
      showModal: () => void
    }
  ): void {
    const clickedMarker = leafletMarkers.find(m => m.id === event.id);

    if (clickedMarker) {
      const markerName = clickedMarker.title || clickedMarker.data?.name || 'Unknown Location';

      callbacks.checkLocationInDistrict(clickedMarker.lat, clickedMarker.lng).then(async districtName => {
        let regionName = '';

        if (districtName) {
          const parentRegionName = await callbacks.checkLocationInRegion(clickedMarker.lat, clickedMarker.lng, true);
          regionName = parentRegionName ? `${parentRegionName} / ${districtName}` : districtName;
        } else {
          const regionNameOnly = await callbacks.checkLocationInRegion(clickedMarker.lat, clickedMarker.lng, true);
          regionName = regionNameOnly || '';
        }

         if (callbacks.onMarkerProcessed) {
          const selectedPoint = {
            name: markerName,
            regionName: regionName,
            lat: clickedMarker.lat,
            lng: clickedMarker.lng,
            coordinates: `${clickedMarker.lat.toFixed(4)}, ${clickedMarker.lng.toFixed(4)}`,
            markerData: clickedMarker.data || null
          };
          callbacks.onMarkerProcessed(selectedPoint, markerName);
        }
      });

       const selectedPointForWeather = {
        name: markerName,
        regionName: '',
        lat: clickedMarker.lat,
        lng: clickedMarker.lng,
        coordinates: `${clickedMarker.lat.toFixed(4)}, ${clickedMarker.lng.toFixed(4)}`,
        markerData: clickedMarker.data || null
      };

      callbacks.onMarkerProcessed(selectedPointForWeather, markerName);
      callbacks.updateCurrentWeatherDate();
      callbacks.getWeatherData(clickedMarker.lat, clickedMarker.lng);
      callbacks.showModal();
    }
  }

  async handleWeatherModalForDblClick(
    event: any,
    monitoringPolygons: any[],
    callbacks: {
      resolveLocationName: (
        event: any,
        polygons: any[],
        checkDistrict: (lat: number, lng: number) => Promise<string | null>,
        checkRegion: (lat: number, lng: number, excludeDistricts: boolean) => Promise<string | null>
      ) => Promise<string>,
      onLocationProcessed: (selectedPoint: any) => void,
      getWeatherData: (lat: number, lng: number) => void,
      updateCurrentWeatherDate: () => void,
      showModal: () => void,
      zoomToRegion: (lat: number, lng: number) => void,
      checkLocationInDistrict: (lat: number, lng: number) => Promise<string | null>,
      checkLocationInRegion: (lat: number, lng: number, excludeDistricts: boolean) => Promise<string | null>
    }
  ): Promise<void> {
    if (event.polygonData) {
      const locationName = await callbacks.resolveLocationName(
        event,
        monitoringPolygons,
        callbacks.checkLocationInDistrict,
        callbacks.checkLocationInRegion
      );

      const selectedPointForWeather = {
        name: 'Новая точка',
        regionName: locationName,
        lat: event.lat,
        lng: event.lng,
        coordinates: `${event.lat.toFixed(6)}, ${event.lng.toFixed(6)}`,
        polygonData: event.polygonData || null
      };

      callbacks.onLocationProcessed(selectedPointForWeather);
      callbacks.updateCurrentWeatherDate();
      callbacks.getWeatherData(event.lat, event.lng);
      callbacks.showModal();
    } else {
      callbacks.zoomToRegion(event.lat, event.lng);
    }
  }

  handleLeafletMapMove(
    event: any,
    mapInstance: any,
    selectedWeatherLayer: string,
    leafletDynamicPolygonsEnabled: boolean,
    callbacks: {
      setIsMapMoving: (moving: boolean) => void,
      loadPolygonsForLeafletView: () => void,
      checkRegionHover: () => void,
      clearSeasonPolygons: () => void,
      clearFieldPolygons: () => void,
      showRegionPolygonCounts: () => void,
      displayAllRegionBoundaries: () => void,
      cleanupPolygonsOutsideViewport: () => void,
      loadSeasonsForCurrentMapArea: () => void,
      checkLocationInRegion: (lat: number, lng: number) => Promise<string | null>,
      updateHoveredRegionName: (regionName: string) => void,
      clearTimeout: (timeoutId: any) => void,
      setTimeout: (callback: () => void, delay: number) => any
    },
    timeoutHandlers: {
      seasonLoadTimeout: any,
      regionCheckTimeout: any,
      setSeasonLoadTimeout: (timeoutId: any) => void,
      setRegionCheckTimeout: (timeoutId: any) => void
    }
  ): void {
    callbacks.setIsMapMoving(true);

    if (leafletDynamicPolygonsEnabled) {
      callbacks.loadPolygonsForLeafletView();
    }

    callbacks.checkRegionHover();

    if (mapInstance?.map) {
      const zoom = mapInstance.map.getZoom();
      callbacks.clearTimeout(timeoutHandlers.seasonLoadTimeout);
      const newSeasonTimeout = callbacks.setTimeout(() => {
        callbacks.setIsMapMoving(false);

        if (zoom < 6) {
          callbacks.clearSeasonPolygons();
          callbacks.clearFieldPolygons();
          callbacks.showRegionPolygonCounts();
        } else if (zoom >= 6 && zoom < 11) {
          callbacks.displayAllRegionBoundaries();
          if (zoom >= 7) {
            callbacks.cleanupPolygonsOutsideViewport();
            callbacks.loadSeasonsForCurrentMapArea();
          }
        } else {
          callbacks.displayAllRegionBoundaries();
          callbacks.cleanupPolygonsOutsideViewport();
          callbacks.loadSeasonsForCurrentMapArea();
        }
      }, 1500);
      timeoutHandlers.setSeasonLoadTimeout(newSeasonTimeout);
    }

    if (selectedWeatherLayer && mapInstance) {
      const bounds = mapInstance.getMapBounds();
      const zoom = mapInstance.getZoom();
      if (bounds && zoom !== undefined) {
        const centerLat = (bounds.north + bounds.south) / 2;
        const centerLon = (bounds.east + bounds.west) / 2;

        const layerMapping: { [key: string]: string } = {
          'precipitation': 'PrecipitationTotal',
          'temperature': 'TemperatureAverage',
          'precipitation_amount': 'PrecipitationTotal',
          'temperature_regime': 'TemperatureMax',
          'precipitation_deviation': 'PrecipitationDeviation',
          'temperature_deviation': 'TemperatureDeviation'
        };

        const apiLayer = layerMapping[selectedWeatherLayer] || 'TemperatureAverage';
      }
    }

    callbacks.clearTimeout(timeoutHandlers.regionCheckTimeout);
    const newRegionTimeout = callbacks.setTimeout(() => {
      const center = mapInstance?.getCenter();
      if (center) {
        callbacks.checkLocationInRegion(center.lat, center.lng).then(regionName => {
          callbacks.updateHoveredRegionName(regionName || '');
        });
      }
    }, 500);
    timeoutHandlers.setRegionCheckTimeout(newRegionTimeout);
  }

  async zoomToRegion(
    lat: number,
    lng: number,
    mapInstance: any,
    callbacks: {
      detectChanges: () => void,
      displayAllRegionBoundaries: () => void,
      findRegionContainingPoint: (lat: number, lng: number) => Promise<any>,
      highlightRegion: (region: any) => void,
      setTimeout: (callback: () => void, delay: number) => void
    }
  ): Promise<void> {
    if (!mapInstance) {
      console.error('Map instance not available');
      return;
    }

    try {
      const currentZoom = mapInstance.map ? mapInstance.map.getZoom() : 10;
      const newZoom = currentZoom + 1;
      mapInstance.setCenter(lat, lng, newZoom);

      callbacks.detectChanges();
      callbacks.displayAllRegionBoundaries();
    } catch (error) {
      console.error('Error calling setCenter:', error);
    }

    callbacks.setTimeout(async () => {
      try {
        const containingRegion = await callbacks.findRegionContainingPoint(lat, lng);

        if (containingRegion) {
          callbacks.highlightRegion(containingRegion);
        }
      } catch (error) {
        console.error('Error finding region:', error);
      }
    }, 500);
  }
}
